<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';

import type { SubAffiliateDealStepPayload } from '@modules/affiliate-program-subaffiliate-deals/submodules/edit/types';
import type {
  VFormFilterButtonWithForm,
  VFormFilterButtonWithFormProps,
} from '@modules/form-widgets/components/VFormButtonsWithFormGroupWidget/types';
import type { FormSchema } from '@modules/forms/components/VForm/types';
import { TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS } from '@modules/affiliate-program-subaffiliate-deals/constants';
import VFormButtonsWithFormGroupWidget from '@modules/form-widgets/components/VFormButtonsWithFormGroupWidget/VFormButtonsWithFormGroupWidget.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { watchImmediate } from '@vueuse/core';

import type {
  SubAffiliateDealsEmits,
  SubAffiliateDealsProps,
} from './types';

const props = withDefaults(defineProps<SubAffiliateDealsProps>(), {});
const emit = defineEmits<SubAffiliateDealsEmits>();

const { getTranslation } = useI18n();

const itemAttributes = computed<SubAffiliateDealStepPayload[]>(() => props.item?.subaffiliate_condition_steps_attributes ?? []);

const innerValues = ref<SubAffiliateDealStepPayload[]>([]);

watchImmediate(itemAttributes, (newValue) => {
  innerValues.value = [...newValue];
}, { deep: true });

const formSchema = computed<FormSchema<SubAffiliateDealStepPayload>>(() => ({
  payment_size: {
    type: EFormInputTypes.NUMBER,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS, 'CONDITION_FORM_VALUE').value,
    required: true,
    validation: {
      minValue: {
        value: 0,
        message: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS, 'PAYMENT_SIZE_INVALID').value,
      },
      maxValue: {
        value: 100,
        message: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS, 'PAYMENT_SIZE_INVALID').value,
      },
    },
  },
  payment_column: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS, 'CONDITION_FORM_PAYMENT_COL').value,
    required: true,
    options: props.paymentOptions ?? [],
  },
}));

const buttons = computed<VFormFilterButtonWithForm<SubAffiliateDealStepPayload>[]>(() => (
  innerValues.value.map((item) => ({
    formSchema: formSchema.value,
    type: 'primary',
    value: item,
    valueSuffix: '%',
  } as VFormFilterButtonWithForm<SubAffiliateDealStepPayload>))
));

const addButton = computed<VFormFilterButtonWithFormProps<SubAffiliateDealStepPayload> | undefined>(() => (!innerValues.value.length
  ? {
      formSchema: formSchema.value,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS, 'STEP_PAYMENT_SIZE_EMPTY').value,
      type: 'primary',
      valueSuffix: '%',
    }
  : undefined));

function onChange(index: number, value: SubAffiliateDealStepPayload): void {
  if (innerValues.value[index]) {
    innerValues.value.splice(index, 1, {
      ...innerValues.value[index],
      ...value,
    });
  }

  emit('change', innerValues.value);
  emit('blur', innerValues.value);
}

function onAdd(value: SubAffiliateDealStepPayload): void {
  innerValues.value.push(value);

  emit('change', innerValues.value);
  emit('blur', innerValues.value);
}
</script>

<template>
  <VFormButtonsWithFormGroupWidget v-data-qa-auto-id="'SubAffiliateDeals'"
    :buttons="buttons"
    :add-button="addButton"
    :errors="errors"
    :readonly="readonly"
    :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS, 'STEP')"
    :form-title="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS, 'CONDITION_MODAL_TITLE')"
    :form-submit-btn="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS, 'CONDITION_MODAL_SAVE')"
    :form-close-btn="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_SUB_AFFILIATE_DEALS, 'CONDITION_MODAL_CANCEL')"
    @change="onChange"
    @add="onAdd"
  />
</template>
